import type { CSSProperties } from 'react';
import {
  CELL_WRAP_MODES,
  DEFAULT_ALIGNMENT,
  MAX_LINES,
  MIN_LINES,
  type CellAlign,
  type CellRule,
  type CellType,
  type CellWrapMode,
  type GridAlignment,
} from './types';

/**
 * Alignment and text wrapping per cell type, ported from TMS
 * `src/lib/gridAlignment.ts`.
 *
 * This module is pure. TMS wrote the setting straight to its single-tenant
 * `ajustes` table from here; in Numtable the value belongs to one organization,
 * so loading and saving live with the settings screen that has an `org_id`.
 * Keeping the rules pure also keeps them testable and reusable from the Worker.
 */

/** Tailwind's `leading-snug`, used to turn "max lines" into a max height in em. */
export const LINE_HEIGHT = 1.375;

// Literal class names, never interpolated, so Tailwind's JIT can see them.
const TEXT_CLASS: Record<CellAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const FLEX_CLASS: Record<CellAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

/** Horizontal alignment class for the cell's text. */
export function textClass(rule: CellRule, columnAlign?: 'left' | 'center' | 'right' | 'auto'): string {
  if (columnAlign && columnAlign !== 'auto') {
    return TEXT_CLASS[columnAlign];
  }
  return TEXT_CLASS[rule.align];
}

/** Alignment for flex containers — a chip that fills the whole cell. */
export function flexClass(rule: CellRule, columnAlign?: 'left' | 'center' | 'right' | 'auto'): string {
  if (columnAlign && columnAlign !== 'auto') {
    return FLEX_CLASS[columnAlign];
  }
  return FLEX_CLASS[rule.align];
}

/**
 * Classes and style for a cell's content box: wraps up to `lines` and scrolls
 * internally past that, or stays on one truncated line when `wrap` is off.
 * `multiline` honours the `\n` inside the value itself.
 */
export function contentStyle(
  rule: CellRule,
  multiline: boolean | string = false,
  wrapMode?: 'wrap' | 'clip' | 'shrink' | 'expand' | 'overflow',
  fittedLines?: number,
): { className: string; style: CSSProperties } {
  wrapMode ??= rule.wrapMode;
  const isWrap = wrapMode ? wrapMode === 'wrap' : rule.wrap;
  const isMultiline = typeof multiline === 'string' ? multiline.includes('\n') : Boolean(multiline);
  // Spills into the empty cells to its right; the cell sets how far (see `overflowSpill`).
  if (wrapMode === 'overflow') return { className: 'whitespace-nowrap overflow-hidden leading-snug', style: {} };
  if (!isWrap) {
    return {
      className: isMultiline ? 'truncate leading-snug whitespace-pre-line' : 'truncate leading-snug',
      style: {},
    };
  }
  return {
    className: `overflow-y-auto overflow-x-hidden leading-snug break-words ${
      isMultiline ? 'whitespace-pre-line' : 'whitespace-normal'
    }`,
    style: { maxHeight: `${((fittedLines ?? rule.lines) * LINE_HEIGHT).toFixed(2)}em` },
  };
}

function normalizeRule(value: unknown, fallback: CellRule): CellRule {
  if (!value || typeof value !== 'object') return fallback;
  const rule = value as Partial<CellRule>;
  const align =
    rule.align === 'left' || rule.align === 'center' || rule.align === 'right'
      ? rule.align
      : fallback.align;
  const lines =
    typeof rule.lines === 'number' && Number.isFinite(rule.lines)
      ? Math.min(MAX_LINES, Math.max(MIN_LINES, Math.round(rule.lines)))
      : fallback.lines;
  const wrapMode = CELL_WRAP_MODES.includes(rule.wrapMode as CellWrapMode) ? rule.wrapMode : undefined;
  return {
    align,
    wrap: wrapMode ? wrapMode === 'wrap' : typeof rule.wrap === 'boolean' ? rule.wrap : fallback.wrap,
    lines,
    ...(wrapMode ? { wrapMode } : {}),
  };
}

/** The wrap mode a cell type gets when its column sets none. */
export function ruleWrapMode(rule: CellRule | undefined, type: CellType): CellWrapMode {
  if (rule?.wrapMode) return rule.wrapMode;
  if (type === 'number') return 'expand';
  return (rule?.wrap ?? true) ? 'wrap' : 'clip';
}

/**
 * Decode the stored jsonb, filling in defaults for whatever is missing or
 * invalid. The stored value arrives from the database, so it is untrusted input
 * like any other — never spread it into the rules unchecked.
 */
export function normalizeAlignment(value: unknown): GridAlignment {
  const stored = (value && typeof value === 'object' ? value : {}) as Partial<
    Record<CellType | 'header', unknown>
  >;
  return {
    header: normalizeRule(stored.header, DEFAULT_ALIGNMENT.header),
    text: normalizeRule(stored.text, DEFAULT_ALIGNMENT.text),
    chip: normalizeRule(stored.chip, DEFAULT_ALIGNMENT.chip),
    number: normalizeRule(stored.number, DEFAULT_ALIGNMENT.number),
    date: normalizeRule(stored.date, DEFAULT_ALIGNMENT.date),
    smartchip: normalizeRule(stored.smartchip, DEFAULT_ALIGNMENT.smartchip),
    autofill: normalizeRule(stored.autofill, DEFAULT_ALIGNMENT.autofill),
    checkbox: normalizeRule(stored.checkbox, DEFAULT_ALIGNMENT.checkbox),
    attachment: normalizeRule(stored.attachment, DEFAULT_ALIGNMENT.attachment),
    barcode: normalizeRule(stored.barcode, DEFAULT_ALIGNMENT.barcode),
    text_document: normalizeRule(stored.text_document, DEFAULT_ALIGNMENT.text_document),
    pivot_table: normalizeRule(stored.pivot_table, DEFAULT_ALIGNMENT.pivot_table),
    reconciliation: normalizeRule(stored.reconciliation, DEFAULT_ALIGNMENT.reconciliation),
  };
}

// Backwards compatibility aliases with TMS
export const claseFlex = flexClass;
export const estiloContenido = contentStyle;
export const claseTexto = textClass;
